// What a dropped connection should say to the person who lost it.
//
// Both SSE clients against /api/ai/designer/generate (lib/designer/streamClient
// for the Lesson Designer, lib/scenario-builder/streamClient for Kula Create)
// used to let `fetch`'s rejection travel straight to the UI. That rejection is
// a bare `TypeError: Failed to fetch` — the same string for a dropped wifi
// connection, a DNS failure and a CORS reject — and it answers none of the
// questions an author actually has. A real author lost a tweak to a wifi blip
// mid-build and was told "Failed to fetch".
//
// The clients keep their own error classes (each carries a `code` its callers
// switch on), so this module classifies and writes the copy; construction stays
// with the caller.

export type TransportFailure = {
  code: 'aborted' | 'offline' | 'network';
  message: string;
};

/**
 * Classify a thrown value as a transport failure, or `null` when it is
 * something else entirely.
 *
 * Deliberately narrow: only the shapes a failed fetch or a severed stream
 * actually throw are claimed. A bug inside a consumer callback would otherwise
 * be reported to the author as "check your internet", sending them to debug
 * their router over our stack trace.
 *
 *   - `TypeError`     — how every engine rejects a failed fetch, and how a
 *                       reader rejects when the connection dies mid-response
 *                       ("Failed to fetch" / "NetworkError…" / "Load failed").
 *   - `DOMException`  — abort signals, plus some engines' network errors.
 */
export function classifyTransportFailure(err: unknown): TransportFailure | null {
  // Read `name` off the value rather than gating on `instanceof Error`:
  // DOMException does not extend Error in every runtime, and an abort is
  // exactly the case that arrives as one.
  const name =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name: unknown }).name)
      : '';

  // A caller-driven abort is not a failure — nobody needs reassuring about it.
  if (name === 'AbortError') {
    return { code: 'aborted', message: 'Cancelled.' };
  }

  const isTransport = err instanceof TypeError || err instanceof DOMException;
  if (!isTransport) return null;

  // `navigator.onLine === false` is conclusive (the browser knows it has no
  // link); `true` proves nothing, so it is only ever used to pick nicer copy.
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  return offline
    ? {
        code: 'offline',
        message: "You're offline. Nothing was lost — reconnect and try again.",
      }
    : {
        code: 'network',
        message:
          'Lost the connection to Kula before that finished. Nothing was changed — check your internet and try again.',
      };
}

/** Copy for an HTTP status the endpoint didn't explain for itself. Only 5xx
 *  gets rewritten: a 4xx without a body is a bug worth seeing the number of. */
export function messageForStatus(status: number): string | null {
  return status >= 500
    ? "Kula's AI service didn't answer. Nothing was changed — try again in a moment."
    : null;
}
